import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/contactless1.jpg";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  RiseOutlined,
  FallOutlined,
  WalletOutlined,
  CheckCircleFilled,
  SafetyCertificateOutlined,
  FileTextOutlined,
  TeamOutlined,
  ApiOutlined,
  ArrowRightOutlined,
  LockOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

const txTypes = [
  {
    key: "deposit",
    label: "Deposit",
    icon: <RiseOutlined />,
    color: "#22c55e",
    title: "SACCO Contributions",
    desc: "Make regular deposits directly to your SACCO account — scheduled or on-demand.",
    steps: [
      "Select 'Financial Services' → 'Pay to SACCO'",
      "Choose your SACCO from verified partners",
      "Select 'Deposit' and enter amount",
      "Confirm and authorise with PIN",
    ],
  },
  {
    key: "repayment",
    label: "Loan Repayment",
    icon: <WalletOutlined />,
    color: blueColor,
    title: "Loan Repayments",
    desc: "Settle loan instalments on time, every time — with instant confirmation and updated records.",
    steps: [
      "Select 'Financial Services' → 'Loan Repayment'",
      "Your active loans are fetched automatically",
      "Choose loan and enter repayment amount",
      "Authorise — balance updates in real time",
    ],
  },
  {
    key: "withdrawal",
    label: "Withdrawal",
    icon: <FallOutlined />,
    color: goldColor,
    title: "Member Withdrawals",
    desc: "Request savings withdrawals directly through OnePayX — processed with full audit records.",
    steps: [
      "Select 'Financial Services' → 'Withdrawal'",
      "Enter withdrawal amount and account",
      "Request approved via role-based workflow",
      "Funds disbursed with digital receipt",
    ],
  },
];

const benefits = [
  {
    icon: <CheckCircleFilled />,
    title: "Instant Confirmation",
    body: "Every transaction generates a reference number and confirmation in under 5 seconds.",
  },
  {
    icon: <ApiOutlined />,
    title: "Secure API Integration",
    body: "SACCO records updated automatically via encrypted API — no manual data entry.",
  },
  {
    icon: <FileTextOutlined />,
    title: "Digital Receipts",
    body: "Downloadable statements and receipts stored permanently in your transaction history.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Reduced Cash Risk",
    body: "Eliminate cash handling, theft exposure, and manual reconciliation errors.",
  },
  {
    icon: <BarChartOutlined />,
    title: "Record Accuracy",
    body: "Every entry is timestamped and immutable — improving audit trails for your SACCO.",
  },
  {
    icon: <TeamOutlined />,
    title: "Member Inclusion",
    body: "Accessible via mobile, web, and USSD — reaching members in any location.",
  },
];

const recentTx = [
  {
    type: "deposit",
    label: "Monthly Contribution",
    sacco: "Kampala Teachers SACCO",
    amount: "+UGX 150,000",
    time: "Today, 09:14",
    color: "#22c55e",
  },
  {
    type: "repayment",
    label: "Loan Repayment #3",
    sacco: "Stanbic SACCO Partners",
    amount: "-UGX 320,000",
    time: "Yesterday, 14:02",
    color: blueColor,
  },
  {
    type: "deposit",
    label: "Top-up Savings",
    sacco: "Kampala Teachers SACCO",
    amount: "+UGX 50,000",
    time: "Mar 8, 11:30",
    color: "#22c55e",
  },
  {
    type: "withdrawal",
    label: "Emergency Withdrawal",
    sacco: "Centenary SACCO",
    amount: "-UGX 200,000",
    time: "Mar 6, 16:45",
    color: goldColor,
  },
];

export default function FinancialServices() {
  return (
    <Wrapper>
      {/* ══ HERO: full-width image band with overlaid content ══ */}
      <HeroBand>
        <HeroImage src={image} alt="SACCO financial services" />
        <HeroOverlay />

        <HeroInner className="container">
          {/* Left: headline */}
          <HeroLeft
            data-aos="fade-right"
            data-aos-duration="750"
            data-aos-delay="100"
          >
            <HeroEyebrow>
              <EyebrowDot />
              Financial Services
            </HeroEyebrow>
            <HeroHeadline>
              SACCO Payments,
              <br />
              <AccentGold>Digitised</AccentGold> &amp;
              <br />
              Secured.
            </HeroHeadline>
            <HeroSubline>
              Digital payments for SACCO contributions, loan repayments, and
              withdrawals — connected directly to your institution.
            </HeroSubline>
            <HeroStats>
              <HStat>
                <HStatVal>360°</HStatVal>
                <HStatLabel>Member visibility</HStatLabel>
              </HStat>
              <HStatDiv />
              <HStat>
                <HStatVal>0</HStatVal>
                <HStatLabel>Manual entries</HStatLabel>
              </HStat>
              <HStatDiv />
              <HStat>
                <HStatVal>&lt;5s</HStatVal>
                <HStatLabel>Per transaction</HStatLabel>
              </HStat>
            </HeroStats>
          </HeroLeft>

          {/* Right: member card + tx feed */}
          <HeroRight
            data-aos="fade-left"
            data-aos-duration="750"
            data-aos-delay="250"
          >
            <MemberCard>
              <MemberCardTop>
                <MemberAvatar>JK</MemberAvatar>
                <MemberInfo>
                  <MemberName>James Kato</MemberName>
                  <MemberSACCO>Kampala Teachers SACCO</MemberSACCO>
                </MemberInfo>
                <MemberBadge>
                  <LockOutlined /> Verified
                </MemberBadge>
              </MemberCardTop>

              <MemberBalance>
                <MemberBalanceLabel>Total Savings Balance</MemberBalanceLabel>
                <MemberBalanceVal>UGX 4,820,000</MemberBalanceVal>
                <MemberBalanceSub>
                  <RiseOutlined style={{ color: "#22c55e" }} />
                  +UGX 200,000 this month
                </MemberBalanceSub>
              </MemberBalance>

              <TxFeedLabel>Recent Transactions</TxFeedLabel>
              <TxFeed>
                {recentTx.map((tx, i) => (
                  <TxRow key={i}>
                    <TxDot $color={tx.color} />
                    <TxMeta>
                      <TxLabel>{tx.label}</TxLabel>
                      <TxSACCO>{tx.sacco}</TxSACCO>
                    </TxMeta>
                    <TxRight>
                      <TxAmount $color={tx.color}>{tx.amount}</TxAmount>
                      <TxTime>{tx.time}</TxTime>
                    </TxRight>
                  </TxRow>
                ))}
              </TxFeed>
            </MemberCard>
          </HeroRight>
        </HeroInner>

        <HeroDiag />
      </HeroBand>

      {/* ══ TRANSACTION TYPES — tab-style cards ══ */}
      <TxTypesSection className="container">
        <TxTypesHeader
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="100"
        >
          <TxTypesEyebrow>How It Works</TxTypesEyebrow>
          <TxTypesTitle>
            Three ways to <GoldSpan>transact</GoldSpan> with your SACCO
          </TxTypesTitle>
        </TxTypesHeader>

        <TxTypesGrid>
          {txTypes.map((tx, i) => (
            <TxTypeCard
              key={tx.key}
              $color={tx.color}
              data-aos="fade-up"
              data-aos-delay={200 + i * 110}
              data-aos-duration="650"
            >
              <TxTypeTopBar $color={tx.color} />
              <TxTypeHead>
                <TxTypeIconWrap $color={tx.color}>{tx.icon}</TxTypeIconWrap>
                <TxTypeLabel $color={tx.color}>{tx.label}</TxTypeLabel>
              </TxTypeHead>
              <TxTypeTitle>{tx.title}</TxTypeTitle>
              <TxTypeDesc>{tx.desc}</TxTypeDesc>
              <TxTypeSteps>
                {tx.steps.map((s, j) => (
                  <TxTypeStep key={j}>
                    <TxTypeStepNum $color={tx.color}>{j + 1}</TxTypeStepNum>
                    <TxTypeStepText>{s}</TxTypeStepText>
                  </TxTypeStep>
                ))}
              </TxTypeSteps>
              <TxTypeCta $color={tx.color}>
                Learn more <ArrowRightOutlined />
              </TxTypeCta>
            </TxTypeCard>
          ))}
        </TxTypesGrid>
      </TxTypesSection>

      {/* ══ BENEFITS — 3-col grid on dark band ══ */}
      <BenefitsBand>
        <BenefitsInner className="container">
          <BenefitsTop
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="100"
          >
            <BenefitsEyebrow>Why It Matters</BenefitsEyebrow>
            <BenefitsTitle>
              Built for <GoldSpan>members</GoldSpan> and{" "}
              <GoldSpan>institutions</GoldSpan>
            </BenefitsTitle>
          </BenefitsTop>

          <BenefitsGrid>
            {benefits.map((b, i) => (
              <BenefitCard
                key={i}
                data-aos="fade-up"
                data-aos-delay={180 + i * 80}
                data-aos-duration="580"
              >
                <BenefitIconWrap>{b.icon}</BenefitIconWrap>
                <BenefitTitle>{b.title}</BenefitTitle>
                <BenefitBody>{b.body}</BenefitBody>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </BenefitsInner>
      </BenefitsBand>
    </Wrapper>
  );
}

/* ── Animations ── */
const fadeRow = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// const pulseDot = keyframes`
//   0%, 100% { transform: scale(1); opacity: 1; }
//   50%       { transform: scale(1.5); opacity: 0.5; }
// `;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  overflow: hidden;
`;

/* Hero */
const HeroBand = styled.div`
  position: relative;
  min-height: 560px;
  overflow: hidden;
  padding-bottom: 72px;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
  display: block;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    rgba(6, 14, 38, 0.93) 0%,
    rgba(6, 14, 38, 0.82) 48%,
    rgba(6, 14, 38, 0.55) 100%
  );
`;

const HeroDiag = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: #ffffff;
  clip-path: polygon(0 100%, 100% 20%, 100% 100%);
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 52px;
  align-items: center;
  padding: 64px 20px 80px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const HeroLeft = styled.div``;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${goldColor};
  background: ${goldColor}18;
  border: 1px solid ${goldColor}35;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 20px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

const HeroHeadline = styled.h2`
  font-size: 44px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.6px;
  line-height: 1.08;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const AccentGold = styled.span`
  color: ${goldColor};
`;

const HeroSubline = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.7;
  margin: 0 0 28px;
  max-width: 440px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
`;

const HStat = styled.div`
  padding: 0 20px;
  &:first-child {
    padding-left: 0;
  }
`;

const HStatVal = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.8px;
  line-height: 1;
`;

const HStatLabel = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-top: 4px;
`;

const HStatDiv = styled.div`
  width: 1px;
  height: 34px;
  background: rgba(255, 255, 255, 0.15);
`;

/* Member card */
const HeroRight = styled.div``;

const MemberCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 22px 22px 0;
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.28);
  overflow: hidden;
`;

const MemberCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
`;

const MemberAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${blueColor} 0%, #3b82f6 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
`;

const MemberSACCO = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const MemberBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  color: #22c55e;
  background: #22c55e12;
  border-radius: 6px;
  padding: 3px 9px;
`;

const MemberBalance = styled.div`
  background: linear-gradient(135deg, #07142e 0%, #0d2254 100%);
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const MemberBalanceLabel = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
`;

const MemberBalanceVal = styled.div`
  font-size: 24px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.8px;
  line-height: 1;
  margin-bottom: 6px;
`;

const MemberBalanceSub = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TxFeedLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
  padding: 0 0;
`;

const TxFeed = styled.div`
  display: flex;
  flex-direction: column;
`;

const TxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
  animation: ${fadeRow} 0.5s ease both;
`;

const TxDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const TxMeta = styled.div`
  flex: 1;
`;

const TxLabel = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
`;

const TxSACCO = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
`;

const TxRight = styled.div`
  text-align: right;
`;

const TxAmount = styled.div<{ $color: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.$color};
`;

const TxTime = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
`;

/* Tx types section */
const TxTypesSection = styled.div`
  padding: 64px 20px 56px;
`;

const TxTypesHeader = styled.div`
  margin-bottom: 40px;
`;

const TxTypesEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 10px;
`;

const TxTypesTitle = styled.h3`
  font-size: 28px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -0.8px;
  margin: 0;
`;

const GoldSpan = styled.span`
  color: ${goldColor};
`;

const TxTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const TxTypeCard = styled.div<{ $color: string }>`
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 20px;
  padding: 0 0 0;
  overflow: hidden;
  position: relative;
  transition: all 0.26s ease;
  cursor: default;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 44px ${(p) => p.$color}18;
    border-color: ${(p) => p.$color}40;
  }
`;

const TxTypeTopBar = styled.div<{ $color: string }>`
  height: 4px;
  background: ${(p) => p.$color};
  width: 100%;
`;

const TxTypeHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px 0;
  margin-bottom: 12px;
`;

const TxTypeIconWrap = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${(p) => p.$color}14;
  color: ${(p) => p.$color};
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TxTypeLabel = styled.div<{ $color: string }>`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${(p) => p.$color};
`;

const TxTypeTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #0a1628;
  padding: 0 22px;
  margin-bottom: 8px;
`;

const TxTypeDesc = styled.div`
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.6;
  padding: 0 22px;
  margin-bottom: 18px;
`;

const TxTypeSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 22px;
  margin-bottom: 18px;
`;

const TxTypeStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const TxTypeStepNum = styled.div<{ $color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${(p) => p.$color}14;
  color: ${(p) => p.$color};
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const TxTypeStepText = styled.div`
  font-size: 12.5px;
  color: #475569;
  line-height: 1.45;
`;

const TxTypeCta = styled.a<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.$color};
  padding: 14px 22px;
  border-top: 1px solid #f1f5f9;
  text-decoration: none;
  margin-top: auto;
  transition:
    gap 0.2s,
    background 0.2s;

  &:hover {
    gap: 10px;
    background: ${(p) => p.$color}06;
  }
`;

/* Benefits band */
const BenefitsBand = styled.div`
  background: linear-gradient(145deg, #07142e 0%, #0e2458 55%, #091a40 100%);
  padding: 64px 0 72px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, ${blueColor}22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const BenefitsInner = styled.div`
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const BenefitsTop = styled.div`
  margin-bottom: 40px;
`;

const BenefitsEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${goldColor};
  margin-bottom: 10px;
`;

const BenefitsTitle = styled.h3`
  font-size: 26px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -0.7px;
  margin: 0;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 22px 20px;
  transition: all 0.24s ease;
  cursor: default;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: ${goldColor}40;
    transform: translateY(-3px);
  }
`;

const BenefitIconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${blueColor}25;
  color: #93c5fd;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const BenefitTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 7px;
`;

const BenefitBody = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.65;
`;

// import React from "react";
// import PageHeader from "../PageHeader";
// import ProductSection from "./ProductSection";
// import saccoImg from "../../../../../assets/img/saving.jpg";

// export default function FinancialServices() {
//   return (
//     <>
//       <PageHeader
//         title="Financial Services"
//         description="Digital payments for SACCO contributions, loan repayments, and withdrawals."
//         backgroundImage={saccoImg}
//         height="65vh"
//       />

//       <ProductSection
//         title="Pay to SACCO: Digital Contributions, Repayments & Withdrawals"
//         subtitle="Secure, Fast, and Transparent"
//         image={saccoImg}
//         imagePosition="left"
//         steps={[
//           "Select 'Financial Services' → 'Pay to SACCO'",
//           "Choose your SACCO from the list of partners",
//           "Select the transaction type (deposit, repayment, withdrawal)",
//           "Enter the amount and confirm",
//           "Authorise with your PIN",
//         ]}
//         highlights={[
//           "Instant confirmation and reference number",
//           "Updated SACCO records via secure API integration",
//           "Digital receipts and downloadable statements",
//           "Reduced cash handling risks",
//           "Improved record accuracy",
//           "Easier member access and inclusion",
//         ]}
//       />
//     </>
//   );
// }
